import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentService } from '@services/document.service';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';
import { useConfirm } from './useConfirm';
import type { DocumentDetailModel } from './useDocumentDetail';

export interface MetadataReExtractionState {
  extractTitle: boolean;
  extractSummary: boolean;
  extractTags: boolean;
  setExtractTitle: (value: boolean) => void;
  setExtractSummary: (value: boolean) => void;
  setExtractTags: (value: boolean) => void;
  reExtracting: boolean;
  // All-false is ACCEPTED by the backend (200, updated_fields: []) — this gate only skips a pointless
  // round trip, not a validation rule (api/routes/documents.py:1515/1525/1561,1581-1583; api/routes/simple_upload.py:1676,:1889).
  nothingSelected: boolean;
  reExtract: () => Promise<void>;
}

export function useMetadataReExtraction(
  id: string | undefined,
  patchDoc: (patch: Partial<DocumentDetailModel>) => void,
): MetadataReExtractionState {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [extractTitle, setExtractTitle] = useState(true);
  const [extractSummary, setExtractSummary] = useState(true);
  const [extractTags, setExtractTags] = useState(true);
  const [reExtracting, setReExtracting] = useState(false);

  const nothingSelected = !extractTitle && !extractSummary && !extractTags;

  const reExtract = useCallback(async () => {
    if (!id) return;

    if (nothingSelected) {
      alert(t('documents.selectAtLeastOneMetadataField'));
      return;
    }

    const confirmed = await confirm({
      title: t('documents.reExtractMetadataConfirmTitle'),
      description: t('documents.reExtractMetadataConfirmMessage'),
      confirmLabel: t('documents.reExtractConfirmAction'),
    });
    if (!confirmed) return;

    try {
      setReExtracting(true);
      const result = await documentService.reExtractMetadata(id, {
        extractTitle,
        extractSummary,
        extractTags,
      });

      if (result.updated_fields.length > 0) {
        patchDoc({
          ...(result.title && { title: result.title }),
          ...(result.summary && { summary: result.summary }),
          ...(result.tags && { tags: result.tags }),
        });
      }

      alert(t('documents.metadataReExtracted', { count: result.updated_fields.length }));
    } catch (error) {
      console.error('Failed to re-extract metadata:', error);
      alert(resolveApiErrorMessage(t, error, { fallbackKey: 'documents.metadataReExtractFailed' }));
    } finally {
      setReExtracting(false);
    }
  }, [id, nothingSelected, extractTitle, extractSummary, extractTags, patchDoc, confirm, t]);

  return {
    extractTitle,
    extractSummary,
    extractTags,
    setExtractTitle,
    setExtractSummary,
    setExtractTags,
    reExtracting,
    nothingSelected,
    reExtract,
  };
}
